import { faCirclePlus, faInfoCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React from "react";
import { Button, Col, Form, Row } from "react-bootstrap";

import { BankAccountType } from "../../../../config/filingConstants";
import { IFormITR1 } from "../../../../config/interfaces";
import { useAppDispatch } from "../../../../redux/Store";
import { showHintModal } from "../../../../redux/slice/HintModalSlice";
import FASvgIcon from "../../../FASvgIcon";
import PreventEditComponent from "../../../PreventEditComponent";
import { SelectInputField, TextInputField } from "../../../fields";

type Props = {};

const BankDetails: React.FC<Props> = (props: Props) => {
  const { values } = useFormikContext<IFormITR1>();

  return (
    <>
      <hr />
      <FieldArray name="AddtnlBankDetails">
        {({ push, remove, form }) => (
          <>
            {values?.AddtnlBankDetails?.length > 0
              ? values?.AddtnlBankDetails?.map((_, index) => (
                  <section className="mb-3 mb-sm-0" key={`bank-${index}`}>
                    {index === 0 && (
                      <p className="leading-para fw-bold text-primary">Primary Bank Account</p>
                    )}

                    {index === 1 && (
                      <p className="leading-para fw-bold text-primary">Secondary Bank Account(s)</p>
                    )}

                    <BankForm
                      index={index}
                      form={form}
                      onRemove={(itemIdx: number) => remove(itemIdx)}
                    />
                  </section>
                ))
              : null}
            <PreventEditComponent>
              <Button
                variant="link"
                className="btn-add-link"
                onClick={() =>
                  push({
                    UseForRefund: "false",
                    IFSCCode: "",
                    BankName: "",
                    BankAccountNo: "",
                    BankAccountType: "Savings"
                  })
                }
              >
                <FASvgIcon icon={faCirclePlus} />
                Add more
              </Button>
            </PreventEditComponent>
          </>
        )}
      </FieldArray>
    </>
  );
};

export default BankDetails;

type BankFormProps = {
  index: number;
  form: FormikProps<IFormITR1>;
  onRemove: (itemIdx: number) => void;
};

const BankForm: React.FC<BankFormProps> = (props: BankFormProps) => {
  const { index, onRemove, form } = props;

  const { setFieldValue, handleBlur, values, touched, errors } = form;

  const dispatch = useAppDispatch();

  return (
    <>
      <Row className="align-items-center">
        <Col sm={6} xl>
          <TextInputField
            label="Bank Name"
            isRequired={index === 0 ? true : false}
            name={`AddtnlBankDetails.${index}.BankName`}
            value={values.AddtnlBankDetails[index].BankName}
            onChange={(value) => {
              setFieldValue(`AddtnlBankDetails.${index}.BankName`, value);
            }}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue(
                `AddtnlBankDetails.${index}.BankName`,
                values.AddtnlBankDetails[index].BankName.trim()
              );
            }}
            isInvalid={
              Boolean(getIn(errors, `AddtnlBankDetails.${index}.BankName`)) &&
              getIn(touched, `AddtnlBankDetails.${index}.BankName`)
            }
            errorMsg={getIn(errors, `AddtnlBankDetails.${index}.BankName`)}
          />
        </Col>
        <Col sm={6} xl>
          <TextInputField
            label={
              <>
                IFSC Code
                <span
                  className="info-wrap"
                  role="button"
                  onClick={() =>
                    dispatch(
                      showHintModal({
                        title: "IFSC Code",
                        componentName: "IFSCCodeHint"
                      })
                    )
                  }
                >
                  <FASvgIcon icon={faInfoCircle} />
                </span>
              </>
            }
            isRequired={index === 0 ? true : false}
            name={`AddtnlBankDetails.${index}.IFSCCode`}
            value={values.AddtnlBankDetails[index].IFSCCode}
            onChange={(value) => {
              setFieldValue(`AddtnlBankDetails.${index}.IFSCCode`, value);
            }}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue(
                `AddtnlBankDetails.${index}.IFSCCode`,
                values.AddtnlBankDetails[index].IFSCCode.trim()
              );
            }}
            isInvalid={
              Boolean(getIn(errors, `AddtnlBankDetails.${index}.IFSCCode`)) &&
              getIn(touched, `AddtnlBankDetails.${index}.IFSCCode`)
            }
            errorMsg={getIn(errors, `AddtnlBankDetails.${index}.IFSCCode`)}
          />
        </Col>
        <Col sm={6} xl>
          <TextInputField
            label="Account Number"
            isRequired={index === 0 ? true : false}
            name={`AddtnlBankDetails.${index}.BankAccountNo`}
            value={values.AddtnlBankDetails[index].BankAccountNo}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue(
                `AddtnlBankDetails.${index}.BankAccountNo`,
                values.AddtnlBankDetails[index].BankAccountNo.trim()
              );
            }}
            onChange={(value) => {
              setFieldValue(`AddtnlBankDetails.${index}.BankAccountNo`, value);
            }}
            isInvalid={
              Boolean(getIn(errors, `AddtnlBankDetails.${index}.BankAccountNo`)) &&
              getIn(touched, `AddtnlBankDetails.${index}.BankAccountNo`)
            }
            errorMsg={getIn(errors, `AddtnlBankDetails.${index}.BankAccountNo`)}
          />
        </Col>
        <Col sm xl>
          <SelectInputField
            placeholder="Select Type"
            name={`AddtnlBankDetails.${index}.BankAccountType`}
            menuZIndex={9}
            label="Type"
            isRequired={index === 0 ? true : false}
            isOptionArrayOfObject={false}
            options={BankAccountType}
            value={[values.AddtnlBankDetails[index].BankAccountType]}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setFieldValue(`AddtnlBankDetails.${index}.BankAccountType`, selectedOption.value);
              } else {
                setFieldValue(`AddtnlBankDetails.${index}.BankAccountType`, undefined);
              }
            }}
            onBlur={handleBlur(`AddtnlBankDetails.${index}.BankAccountType`)}
            isInvalid={
              Boolean(getIn(errors, `AddtnlBankDetails.${index}.BankAccountType`)) &&
              getIn(touched, `AddtnlBankDetails.${index}.BankAccountType`)
            }
            errorMsg={getIn(errors, `AddtnlBankDetails.${index}.BankAccountType`)}
          />
        </Col>

        {index > 0 ? (
          <PreventEditComponent>
            <Col sm="auto" className="ps-0">
              {/* visible in other than small view (tab, laptop etc) */}
              <Button
                variant="transparent"
                className="text-greyDark icon-only d-none d-sm-flex"
                onClick={() => onRemove(index)}
              >
                <FASvgIcon icon={faTrash} />
              </Button>
              {/* visible in small view (mobile) */}
              <Button
                variant="transparent"
                className="btn-delete-full d-sm-none"
                onClick={() => onRemove(index)}
              >
                <FASvgIcon icon={faTrash} />
                <span>Delete</span>
              </Button>
            </Col>
          </PreventEditComponent>
        ) : null}
      </Row>
      {index === 0 && (
        <Row
          className={classNames(
            (Boolean(getIn(errors, `AddtnlBankDetails.0.BankName`)) &&
              getIn(touched, `AddtnlBankDetails.0.BankName`)) ||
              (Boolean(getIn(errors, `AddtnlBankDetails.0.IFSCCode`)) &&
                getIn(touched, `AddtnlBankDetails.0.IFSCCode`)) ||
              (Boolean(getIn(errors, `AddtnlBankDetails.0.BankAccountNo`)) &&
                getIn(touched, `AddtnlBankDetails.0.BankAccountNo`))
              ? "mb-4 mt-n2"
              : "mt-n4 mb-4"
          )}
        >
          <Col>
            <Form.Text className="field-text text-greyDark m-0">
              Your primary bank account is where you will receive your refunds, if eligible.
            </Form.Text>
          </Col>
        </Row>
      )}
      {index !== values.AddtnlBankDetails.length - 1 && index !== 0 && (
        <hr className="d-block d-xl-none my-4" />
      )}
    </>
  );
};
